'use client';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { useState } from "react";
import { Button } from "../buttons/button";
import { analytics } from "@/app/analytics";
import ExclamationTriangle from "@/assets/icons/exclamation-triangle.svg";
import { Loader2 } from "lucide-react";
import React from "react";
import { MainGreenButton } from "../buttons/main-green-button";
import { Subscription } from "@/app/types/Subscription";
import { useUserDataContext } from "@/components/navigation/contexts/user-data-context";

export interface CancelProPlanModalProps {
    onExtendTrial?: () => Promise<void>;
    onClose: () => void;
    subscription: Subscription;
    onCancel: () => Promise<void>;
}

function CancelProPlanModal({ onExtendTrial, onClose, subscription, onCancel }: CancelProPlanModalProps) {
    const [opened, setOpened] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const { balance: userBalance } = useUserDataContext();

    // Calculate if trial can be extended (trial duration <= 7 days)
    const canExtend = (() => {
        const trialEnd = subscription.current_period_end;
        const trialStart = subscription.current_period_start;
        if (!trialEnd || !trialStart) return false;

        const trialDurationInSeconds = trialEnd - trialStart;
        const sevenDaysInSeconds = 7 * 24 * 60 * 60;
        return trialDurationInSeconds <= sevenDaysInSeconds;
    })();

    const onOpenChange = () => {
        if (opened) {
            analytics.getTrackerInstance().companyPageUnlockClosed({})
        } else {
            analytics.getTrackerInstance().companyPageUnlockOpened({})
        }

        setOpened(!opened);
        onClose();
    }

    const handleExtendTrial = async () => {
        if (onExtendTrial) {
            setIsLoading(true);
            try {
                await onExtendTrial();
            } finally {
                setIsLoading(false);
                setOpened(false);
                onClose();
            }
        }
    }

    const handleCancel = async () => {
        setIsLoading(true);
        try {
            await onCancel();
        } finally {
            setIsLoading(false);
            setOpened(false);
            onClose();
        }
    }

    return (
        <Dialog open={opened} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#FBFBF9] max-w-[600px] md:px-[95px] md:py-[56px] z-modal">
                <DialogHeader>
                    <div className="justify-center items-center gap-2.5 inline-flex">
                        <div className="flex-col justify-start items-center gap-8 inline-flex">
                            <div className='flex p-3 h-16 w-16 items-center justify-center bg-white rounded-lg border border-[#d7d5d2]'>
                                <ExclamationTriangle className="w-full h-full" />
                            </div>
                            <div className="flex-col justify-start items-center gap-4 flex">
                                <DialogTitle className="w-96 text-center text-gray-900 text-3xl font-semibold">
                                    Cancel Pro Plan
                                </DialogTitle>
                                <DialogDescription className="w-96 text-center text-slate-600 text-base font-normal">
                                    {canExtend ? (
                                        userBalance < 25
                                            ? "We hate to see you leave. Get 25 credits to keep exploring companies."
                                            : "We hate to see you leave. Would you like to extend your trial for 7 more days?"
                                    ) : (
                                        "We hate to see you leave, but don't worry, you still have our limited plan."
                                    )}
                                </DialogDescription>
                            </div>
                            <div className="flex-col justify-start items-center gap-4 flex w-full">
                                <div className="w-full gap-2 flex flex-col">
                                    {canExtend ? (
                                        <MainGreenButton
                                            onClick={handleExtendTrial}
                                            disabled={isLoading}
                                            className="w-full items-center justify-center"
                                        >
                                            {isLoading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                    <span>Extending trial...</span>
                                                </>
                                            ) : (
                                                <span>
                                                    {userBalance < 25
                                                        ? "Extend trial for 7-days and get 25 credits"
                                                        : "Extend trial for 7 more days"
                                                    }
                                                </span>
                                            )}
                                        </MainGreenButton>
                                    ) : (
                                        <MainGreenButton
                                            className="w-full items-center justify-center"
                                            onClick={handleCancel}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                    <span>Canceling subscription...</span>
                                                </>
                                            ) : (
                                                "I understand"
                                            )}
                                        </MainGreenButton>
                                    )}
                                </div>
                                {canExtend && (
                                    <Button
                                        variant="ghost"
                                        className="text-gray-600"
                                        onClick={handleCancel}
                                        disabled={isLoading}
                                    >
                                        Cancel Subscription
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogHeader>
            </DialogContent>
        </Dialog>
    );
}

export default CancelProPlanModal; 